import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import cn from "classnames";
import "./fileUpload.scss";
import Dialog from "./Dialog";

export const SingleFileUpload = ({
  filename,
  setFile,
  upload,
}: {
  filename: string;
  setFile: (f: File) => void;
  upload: () => Promise<void>;
}) => {
  const [isUploading, setIsUploading] = useState(false);

  const handleUpload = async () => {
    setIsUploading(true);
    await upload();
    setIsUploading(false);
  };
  return (
    <form className="horizontal m16">
      <input
        key={filename}
        className="single-upload"
        type="file"
        required
        onChange={(e) => {
          if (e.target.files) {
            setFile(e.target.files[0]);
          }
        }}
        accept=".pdf, .doc, .docx, .ppt, .pptx, .xls, .xlsx, .hwp, .jpg, .jpeg, .png, .zip"
      />
      <button type="button" onClick={handleUpload} disabled={isUploading}>
        업로드
      </button>
    </form>
  );
};

const allowedTypes = ["application/pdf", "image/png", "image/jpeg"];

export const MultiFileUpload = ({
  files,
  filename,
  setFiles,
  upload,
  strictFileType,
}: {
  files?: File[];
  filename: string;
  setFiles: (files: File[]) => void;
  upload?: () => Promise<void>;
  strictFileType?: boolean;
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<HTMLLabelElement | null>(null);

  const onChangeFiles = useCallback(
    (e: ChangeEvent<HTMLInputElement> | any) => {
      if (e.type === "drop") {
        if (strictFileType) {
          const filteredFiles = [...e.dataTransfer.files].filter((file: File) =>
            allowedTypes.includes(file.type)
          );
          if (filteredFiles.length) setFiles(filteredFiles);
        } else {
          setFiles(e.dataTransfer.files);
        }
      } else {
        setFiles(e.target.files);
      }
    },
    [setFiles]
  );

  const handleDragIn = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragOut = useCallback((e: DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();

    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer!.files) {
      setIsDragging(true);
    }
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent): void => {
      e.preventDefault();
      e.stopPropagation();

      onChangeFiles(e);
      setIsDragging(false);
    },
    [onChangeFiles]
  );

  const initDragEvents = useCallback((): void => {
    if (dragRef.current !== null) {
      dragRef.current.addEventListener("dragenter", handleDragIn);
      dragRef.current.addEventListener("dragleave", handleDragOut);
      dragRef.current.addEventListener("dragover", handleDragOver);
      dragRef.current.addEventListener("drop", handleDrop);
    }
  }, [handleDragIn, handleDragOut, handleDragOver, handleDrop]);

  const resetDragEvents = useCallback((): void => {
    if (dragRef.current !== null) {
      dragRef.current.removeEventListener("dragenter", handleDragIn);
      dragRef.current.removeEventListener("dragleave", handleDragOut);
      dragRef.current.removeEventListener("dragover", handleDragOver);
      dragRef.current.removeEventListener("drop", handleDrop);
    }
  }, [handleDragIn, handleDragOut, handleDragOver, handleDrop]);

  useEffect(() => {
    initDragEvents();

    return () => {
      resetDragEvents();
    };
  }, []);

  const handleUpload = useCallback(async () => {
    setIsUploading(true);
    try {
      await upload?.();
    } catch {
      alert("업로드에 실패했습니다.");
    } finally {
      setIsUploading(false);
    }
  }, [upload]);

  return (
    <div className="multi-upload-wrapper">
      <input
        key={filename}
        id={`fileUpload${filename}`}
        type="file"
        style={{ display: "none" }}
        onChange={onChangeFiles}
        multiple
        accept={strictFileType ? ".pdf, .jpg, .jpeg, .png" : undefined}
      />
      <label
        htmlFor={`fileUpload${filename}`}
        className={cn("multi-upload", { isDragging })}
        ref={dragRef}
      >
        {files ? (
          Array.from(files).map((file) => <p>{file.name}</p>)
        ) : (
          <div>파일을 선택하세요.</div>
        )}
      </label>
      {upload && (
        <button onClick={handleUpload} disabled={!files?.length || isUploading}>
          업로드
        </button>
      )}
    </div>
  );
};

export const FileUploadDialog = ({
  title,
  upload,
  close,
  isMulti,
}: {
  title: string;
  upload: (files: File[]) => Promise<void>;
  close: () => void;
  isMulti?: boolean;
}) => {
  const [files, setFiles] = useState<File[]>();

  const handleUpload = async () => {
    if (!files) {
      alert("파일을 선택해 주세요.");
      return;
    }

    const isDuplicateName = Array.from(files).some((file, index) => {
      return (
        Array.from(files).findIndex(
          (f, i) => f.name === file.name && i !== index
        ) !== -1
      );
    });

    if (isDuplicateName) {
      alert("중복된 파일 이름이 있습니다.");
      return;
    }

    try {
      await upload(files);
      alert("성공적으로 업로드되었습니다.");
      close();
    } catch {
      alert("업로드에 실패했습니다.");
    }
  };

  return (
    <Dialog
      title={`${title} 업로드`}
      buttons={["ok", "close"]}
      ok={handleUpload}
      close={close}
    >
      {isMulti ? (
        <MultiFileUpload files={files} filename="title" setFiles={setFiles} />
      ) : (
        <form className="horizontal m16">
          <input
            className="single-upload"
            type="file"
            required
            onChange={(e) => {
              if (e.target.files) {
                setFiles([e.target.files[0]]);
              }
            }}
            accept=".pdf, .doc, .docx, .ppt, .pptx, .xls, .xlsx, .hwp, .jpg, .jpeg, .png, .zip"
          />
        </form>
      )}
    </Dialog>
  );
};
